import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { theme } from '../../../theme';
import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AadharCardFormModal from '../../../components/Modals/AadharCardFormModal';
import { useTranslation } from 'react-i18next';
import { useStudents } from '../../../hooks/useStudents';
import { useClasses } from '../../../hooks/useClasses';
import { useSessions } from '../../../hooks/useSession';
import { useSchools } from '../../../hooks/useSchools';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import AadharCardTemplate from '../../../components/templates/AadharCardTemplate';
import { useAuth, AuthContext } from '../../../contexts/AuthContext';
import { useSessionClass } from "../../../contexts/SessionClassContext";
import { useError } from '../../../contexts/ErrorContext';
import axios from 'axios';
import { getStudentData } from '../../../utils/storage';

// ------------------------------
// QueryClient for bulk generation
// ------------------------------
const bulkQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// ------------------------------
// Get real user data from localStorage
// ------------------------------
const getRealAuthValue = () => {
  try {
    const token = localStorage.getItem('token');
    const userString = localStorage.getItem('user');
    const tenantSchema = localStorage.getItem('tenantSchema');

    if (token && userString) {
      const userData = JSON.parse(userString);

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (tenantSchema) {
        axios.defaults.headers.common['X-Tenant-ID'] = tenantSchema;
      }

      return {
        user: userData,
        isAuthenticated: true,
        isLoading: false,
        wasAutoLoggedOut: false,
        login: async () => userData,
        register: async () => userData,
        forgotPassword: async () => '',
        verifyOtp: async () => '',
        resetPassword: async () => '',
        logout: () => {},
        clearAutoLogoutFlag: () => {},
        isAdmin: () => userData.roles?.includes('ADMIN') || false,
        isSchoolAdmin: () => userData.roles?.includes('SCHOOL_ADMIN') || false,
        isTeacher: () => userData.roles?.includes('TEACHER') || false,
        isStudent: () => userData.roles?.includes('STUDENT') || false
      };
    }
  } catch (error) {
  }

  const fallbackUser = {
    id: 1,
    username: 'admin',
    email: 'admin@example.com',
    fullName: 'Administrator',
    schoolId: 1,
    roles: ['ADMIN']
  };

  return {
    user: fallbackUser,
    isAuthenticated: true,
    isLoading: false,
    wasAutoLoggedOut: false,
    login: async () => fallbackUser,
    register: async () => fallbackUser,
    forgotPassword: async () => '',
    verifyOtp: async () => '',
    resetPassword: async () => '',
    logout: () => {},
    clearAutoLogoutFlag: () => {},
    isAdmin: () => true,
    isSchoolAdmin: () => false,
    isTeacher: () => false,
    isStudent: () => false
  };
};

// ------------------------------
// Template wrapper with providers
// ------------------------------
const AadharCardTemplateWithContext: React.FC<{ data: any; hidePrintButton?: boolean; schoolData?: any }> = ({ data, hidePrintButton, schoolData }) => {
  const authValue = getRealAuthValue();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={bulkQueryClient}>
        <AuthContext.Provider value={authValue}>
          <AadharCardTemplate data={data} hidePrintButton={hidePrintButton} schoolData={schoolData} />
        </AuthContext.Provider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

// ------------------------------
// Utilities for reliable rendering
// ------------------------------
const waitNextFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => setTimeout(resolve, 0));
  });

const waitForImages = async (el: HTMLElement) => {
  const imgs = Array.from(el.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((res) => {
          if (img.complete && img.naturalWidth) return res();
          img.addEventListener("load", () => res(), { once: true });
          img.addEventListener("error", () => res(), { once: true });
        })
    )
  );
};

// Enhanced function to wait for all images including those loaded via fetch
const waitForAllImages = async (el: HTMLElement) => {
  // Wait for initial images
  await waitForImages(el);

  // Wait for any dynamically loaded images (like student photos)
  let attempts = 0;
  const maxAttempts = 20; // Wait up to 2 seconds

  while (attempts < maxAttempts) {
    const imgs = Array.from(el.querySelectorAll("img"));
    const loadingImgs = imgs.filter(img => !img.complete || img.naturalWidth === 0);

    if (loadingImgs.length === 0) {
      break; // All images are loaded
    }

    // Wait for any remaining images
    await Promise.all(
      loadingImgs.map(
        (img) =>
          new Promise<void>((res) => {
            if (img.complete && img.naturalWidth) return res();
            img.addEventListener("load", () => res(), { once: true });
            img.addEventListener("error", () => res(), { once: true });
          })
      )
    );

    attempts++;
    await new Promise(resolve => setTimeout(resolve, 100)); // Wait 100ms between checks
  }
};

const waitForFonts = async () => {
  try {
    // @ts-ignore
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {
    /* ignore */
  }
};

const hasRenderableContent = (node: HTMLElement) => {
  const txt = node.textContent?.trim() ?? "";
  return txt.length > 20 || node.children.length > 0;
};

const A4_MM = { w: 210, h: 297 };

const renderCardOnce = async (
  data: any,
  TemplateWithProviders: React.FC<any>
): Promise<HTMLDivElement> => {
  const host = document.createElement("div");
  Object.assign(host.style, {
    position: "absolute",
    left: "-10000px",
    top: "0",
    width: "210mm",
    height: "297mm",
    background: "#fff",
    margin: "0",
    padding: "0",
    boxSizing: "border-box",
    overflow: "hidden",
  } as CSSStyleDeclaration);
  document.body.appendChild(host);

  const root = createRoot(host);
  root.render(
    React.createElement(TemplateWithProviders, {
      data,
      hidePrintButton: true,
    })
  );

  // Let React + MUI paint, then wait fonts & images
  await waitNextFrame();
  await waitNextFrame();
  await waitForFonts();
  await waitForAllImages(host); // Use enhanced image waiting
  await waitNextFrame();

  // Fallback retries if template injects async content
  if (!hasRenderableContent(host)) {
    for (let i = 0; i < 4; i++) {
      await waitNextFrame();
      await waitForAllImages(host); // Use enhanced image waiting
      if (hasRenderableContent(host)) break;
    }
  }

  return host;
};

// Just remove the node (React 18 createRoot is not stored globally here)
const removeHost = (host: HTMLDivElement) => {
  host.remove();
};

// ------------------------------
// Main Component
// ------------------------------
const AadharCardManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user, isSchoolAdmin } = useAuth();
  const { showError, showSuccess } = useError();
  const isStudent = user?.roles?.includes('STUDENT') ?? false;
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkSelectedClass, setBulkSelectedClass] = useState<number | string | ''>('');
  const [bulkSelectedSession, setBulkSelectedSession] = useState<number | ''>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [lastStatusCode, setLastStatusCode] = useState<number | null>(null);
  const [bulkTemplates, setBulkTemplates] = useState<any[]>([]);
  const [showBulkPreview, setShowBulkPreview] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStudent, setCurrentStudent] = useState('');
  const [includeCertifierValues, setIncludeCertifierValues] = useState(true);
  const [fetchedSchoolName, setFetchedSchoolName] = useState('');
  const isBulkDownloadCancelledRef = useRef(false);
  const [bulkSelectedStudentIds, setBulkSelectedStudentIds] = useState<Set<number>>(new Set());

  // Use user.schoolId directly for all roles to ensure correct school ID
  const effectiveSchoolId = user?.schoolId ?? undefined;
  const schoolIdForNameFetch = effectiveSchoolId ?? 1;

  useEffect(() => {
    let isActive = true;

    const fetchSchoolName = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/schoolErp';
        const response = await axios.get(`${baseUrl}/schools/${schoolIdForNameFetch}`);
        const apiSchoolName = response?.data?.nameofSchool;
        if (isActive && typeof apiSchoolName === 'string') {
          setFetchedSchoolName(apiSchoolName);
        }
      } catch (_) {
        // Keep existing fallback behavior if direct API call fails.
      }
    };

    fetchSchoolName();

    return () => {
      isActive = false;
    };
  }, [schoolIdForNameFetch]);

  const {
    useStudentsBySessionIdAndClassId,
    useStudentsBySessionIdAndClassName,
    getStudentByStudentCode,
    getStudentsByStudentCodesBulk,
    getStudentPhotoInfosBulk,
  } = useStudents();
  const { classesQuery } = useClasses(effectiveSchoolId);
  const { sessionsQuery } = useSessions(effectiveSchoolId);
  const { useSchoolById } = useSchools(false, { disableSchoolAdmins: true });
  const {
    selectedClass: layoutSelectedClass,
    selectedSession: layoutSelectedSession,
  } = useSessionClass();
  const layoutSelectedClassId =
    layoutSelectedClass && layoutSelectedClass.id != null
      ? Number(layoutSelectedClass.id)
      : undefined;
  const layoutSelectedSessionId =
    layoutSelectedSession && layoutSelectedSession.id != null
      ? Number(layoutSelectedSession.id)
      : undefined;

  const classes = classesQuery.data || [];
  const sessions = sessionsQuery.data || [];
  const classesLoading = classesQuery.isLoading;
  const classesError = classesQuery.error;
  const { data: schoolData } = useSchoolById(effectiveSchoolId);
  const bulkSchoolData = schoolData
    ? { ...schoolData, nameofSchool: fetchedSchoolName || schoolData?.nameofSchool }
    : fetchedSchoolName
      ? { nameofSchool: fetchedSchoolName }
      : undefined;

  const normalizedBulkClassId =
    typeof bulkSelectedClass === "number" && !Number.isNaN(bulkSelectedClass) ? bulkSelectedClass : undefined;

  const normalizedBulkClassName =
    typeof bulkSelectedClass === "string" && bulkSelectedClass.trim().length > 0 ? bulkSelectedClass : undefined;

  const normalizedBulkSession =
    typeof bulkSelectedSession === "number" && !Number.isNaN(bulkSelectedSession)
      ? bulkSelectedSession
      : undefined;

  const studentsByIdQuery = useStudentsBySessionIdAndClassId(
    isStudent ? undefined : normalizedBulkSession,
    isStudent ? undefined : normalizedBulkClassId
  );
  const studentsByNameQuery = useStudentsBySessionIdAndClassName(
    isStudent ? undefined : normalizedBulkSession,
    isStudent ? undefined : normalizedBulkClassName
  );
  const studentsQuery = normalizedBulkClassName ? studentsByNameQuery : studentsByIdQuery;
  const { data: students = [], isLoading: studentsLoading } = studentsQuery;

  const bulkVisibleStudents = useMemo(() => {
    return students
      .filter((s: any) => s.status === true && typeof s.id === "number")
      .slice()
      .sort((a: any, b: any) =>
        String((a as any).studentName || (a as any).name || "").localeCompare(
          String((b as any).studentName || (b as any).name || "")
        )
      );
  }, [students]);

  useEffect(() => {
    setBulkSelectedStudentIds(
      new Set(bulkVisibleStudents.map((s: any) => s.id as number))
    );
  }, [normalizedBulkSession, normalizedBulkClassId, normalizedBulkClassName, bulkVisibleStudents]);

  const bulkSelectAllChecked =
    bulkVisibleStudents.length > 0 &&
    bulkVisibleStudents.every((s: any) => bulkSelectedStudentIds.has(s.id));
  const bulkSelectAllIndeterminate =
    bulkSelectedStudentIds.size > 0 && !bulkSelectAllChecked;

  const getClassDisplayName = (classId?: number | null) => {
    if (!classId) return t('unknown_class');
    const cls = classes.find((c: any) => c.id === classId);
    if (!cls) return t('unknown_class');
    return cls.section ? `${cls.name} ${cls.section}` : cls.name;
  };

  const getClassSortValue = (className?: string) => {
    if (!className) return Number.MAX_SAFE_INTEGER;
    const normalized = className.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

    // Ensure Nursery -> LKG -> UKG always appear before Class 1.
    if (normalized.includes("nursery")) return -3;
    if (normalized.includes("lkg")) return -2;
    if (normalized.includes("ukg")) return -1;

    const numericMatch = className.match(/\d+/);
    if (numericMatch) return parseInt(numericMatch[0], 10);

    return Number.MAX_SAFE_INTEGER;
  };

  const getSectionSortValue = (section?: string | null) => {
    if (!section) return 0;
    const normalized = section.trim().toUpperCase();
    if (normalized.length === 0) return 0;

    const charCode = normalized.charCodeAt(0);
    if (charCode >= 65 && charCode <= 90) {
      return charCode - 64; // A=1, B=2, C=3, etc.
    }
    return Number.MAX_SAFE_INTEGER;
  };

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleOpenBulkModal = () => {
    if (layoutSelectedSessionId) {
      setBulkSelectedSession(layoutSelectedSessionId);
    }
    if (layoutSelectedClassId) {
      setBulkSelectedClass(layoutSelectedClassId);
    }
    setBulkModalOpen(true);
    setError('');
    setSuccessMessage('');
    setLastStatusCode(null);
  };

  const handleCloseBulkModal = () => {
    if (isGenerating) return;
    setBulkModalOpen(false);
    setBulkSelectedClass('');
    setBulkSelectedSession('');
    setError('');
    setSuccessMessage('');
    setLastStatusCode(null);
    setProgress(0);
    setCurrentStudent('');
    setIncludeCertifierValues(true);
    setBulkSelectedStudentIds(new Set());
    isBulkDownloadCancelledRef.current = false;
  };

  const handleCancelBulkDownload = () => {
    isBulkDownloadCancelledRef.current = true;
    setIsGenerating(false);
    setProgress(0);
    setCurrentStudent('');
    showError(t("bulk_download_cancelled") || "Bulk download was cancelled by user");
  };

  // normalize axios/fetch response (kept for completeness)
  const extractStatus = (resOrErr: any): { status?: number; ok: boolean } => {
    const status =
      resOrErr?.status ??
      resOrErr?.response?.status ??
      (typeof resOrErr?.ok === 'boolean' && resOrErr.ok ? 200 : undefined);

    const ok =
      typeof status === 'number'
        ? status >= 200 && status < 300
        : !!(resOrErr?.ok || resOrErr?.success);

    return { status, ok };
  };

  // ------------------------------
  // FIXED: robust bulk PDF generator
  // ------------------------------
  const handleBulkDownload = async () => {
    setError('');
    setSuccessMessage('');
    setLastStatusCode(null);
    setProgress(0);
    setCurrentStudent('');

    if ((!normalizedBulkClassId && !normalizedBulkClassName) || !normalizedBulkSession) {
      setError(t('please_select_both_class_session_aadhar'));
      return;
    }

    if (bulkVisibleStudents.length === 0) {
      const className =
        normalizedBulkClassName || getClassDisplayName(normalizedBulkClassId) || t('unknown_class');
      const sessionName = sessions.find((s: any) => s.id === normalizedBulkSession)?.name || t('unknown_session');

      // Ensure proper translation with placeholders
      const translatedMsg = t('no_students_found_aadhar', {
        className,
        sessionName
      });
      let errorMsg: string = typeof translatedMsg === 'string' ? translatedMsg : '';

      // Fallback if translation doesn't replace placeholders
      if (!errorMsg || errorMsg.includes('{className}') || errorMsg.includes('{sessionName}')) {
        const fallbackMsg = 'No students found for the selected class and session. Please verify that students are enrolled in {className} for session {sessionName}';
        const baseTrans = t('no_students_found_aadhar');
        const baseTranslation: string = (baseTrans && typeof baseTrans === 'string') ? baseTrans : fallbackMsg;
        errorMsg = baseTranslation.replace(/{className}/g, String(className)).replace(/{sessionName}/g, String(sessionName));
      }

      // Clear the blue info message and show red error snackbar in top right
      setError('');
      setSuccessMessage('');
      showError(errorMsg);
      return;
    }

    const activeStudents = bulkVisibleStudents.filter((s: any) =>
      bulkSelectedStudentIds.has(s.id)
    );

    if (activeStudents.length === 0) {
      showError(
        (t("please_select_students_to_download") as string) ||
          "Please select at least one student to download."
      );
      return;
    }

    setIsGenerating(true);
    isBulkDownloadCancelledRef.current = false;

    try {
      const className = normalizedBulkClassName || getClassDisplayName(normalizedBulkClassId);
      const sessionName = sessions.find((s: any) => s.id === normalizedBulkSession)?.name || t('unknown');
      const filename = `bulk-aadhaar-cards-${className}-${sessionName}-${activeStudents.length}-students.pdf`;
      const BulkAadharTemplateWithContext: React.FC<{ data: any; hidePrintButton?: boolean }> = (props) => (
        <AadharCardTemplateWithContext {...props} schoolData={bulkSchoolData} />
      );

      // Create the PDF (use mm to match our A4 sizing)
      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
        putOnlyUsedFonts: true,
      });

      let pageIndex = 0;

      // ------------------------------
      // Prefetch: bulk student details (avoid per-student /studentCode calls)
      // ------------------------------
      const uniqueCodes = Array.from(
        new Set(
          activeStudents
            .map((s: any) => s?.studentCode)
            .filter((c: any) => typeof c === "string" && c.trim().length > 0)
        )
      );

      let bulkStudentsByCode = new Map<string, any>();
      try {
        if (uniqueCodes.length > 0) {
          const bulkStudents = await getStudentsByStudentCodesBulk(uniqueCodes);
          (bulkStudents || []).forEach((st: any) => {
            if (st?.studentCode) bulkStudentsByCode.set(String(st.studentCode), st);
          });
        }
      } catch (_) {
        // Keep existing fallback behavior (per-student fetch) if bulk API fails.
      }

      // ------------------------------
      // Prefetch: bulk photo info (avoid per-student /{id}/studentPhoto calls)
      // ------------------------------
      let bulkPhotoInfoById = new Map<number, { fileName?: string; signature?: string }>();
      try {
        const idsToFetch = Array.from(
          new Set(
            (Array.from(bulkStudentsByCode.values()) as any[])
              .map((s) => s?.id)
              .filter((id) => typeof id === "number" && !Number.isNaN(id))
          )
        ) as number[];

        if (idsToFetch.length > 0) {
          const photoInfos = await getStudentPhotoInfosBulk(idsToFetch);
          (photoInfos || []).forEach((pi: any) => {
            const sid = Number(pi?.studentId);
            if (!Number.isNaN(sid)) {
              bulkPhotoInfoById.set(sid, {
                fileName: pi?.fileName || "",
                signature: pi?.signature || "",
              });
            }
          });
        }
      } catch (_) {
        // If bulk photo info fails, template will fall back to existing per-student hooks.
      }

      // Helper: build auth headers for image fetch
      const buildImageHeaders = (): Record<string, string> => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const tenantSchema = localStorage.getItem("tenantSchema") || sessionStorage.getItem("tenantSchema");
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        if (tenantSchema) headers["X-Tenant-ID"] = tenantSchema;
        return headers;
      };

      const fetchBlobUrlForFileName = async (fileName?: string): Promise<string | null> => {
        if (!fileName || typeof fileName !== "string" || fileName.trim().length === 0) return null;
        const base = process.env.REACT_APP_API_URL || "http://localhost:8080/schoolErp";
        const url = `${base}/students/photo/${fileName}`;
        try {
          const res = await fetch(url, { headers: buildImageHeaders() });
          if (!res.ok) return null;
          const blob = await res.blob();
          return URL.createObjectURL(blob);
        } catch {
          return null;
        }
      };

      for (let i = 0; i < activeStudents.length; i++) {
        // Check for cancellation
        if (isBulkDownloadCancelledRef.current) {
          break;
        }
        const student = activeStudents[i];

        // Update current student being processed
        setCurrentStudent((student as any).studentName || (student as any).name || t('unknown_student'));

        let studentData;

        try {
          const code = (student as any).studentCode ? String((student as any).studentCode) : "";
          if (code && bulkStudentsByCode.has(code)) {
            studentData = bulkStudentsByCode.get(code);
          } else if (code) {
            // Fallback: per-student API if bulk details weren't available
            const completeStudentData = await getStudentByStudentCode(code);
            studentData = completeStudentData;
          } else {
            studentData = student;
          }
        } catch (error) {
          studentData = student;
        }

        // Map student data → template props
        const fullName = (studentData as any).studentName || (studentData as any).name || t('unknown_student');
        const village = (studentData as any).city || (studentData as any).village || t('');

        // Convert date array → YYYY-MM-DD
        let dateOfBirth = '';
        if (Array.isArray((studentData as any).dateOfBirth) && (studentData as any).dateOfBirth.length === 3) {
          const [year, month, day] = (studentData as any).dateOfBirth;
          dateOfBirth = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        } else if ((studentData as any).dateOfBirth) {
          dateOfBirth = (studentData as any).dateOfBirth;
        }

        // Aadhaar number as string
        let aadhaarNumber = '';
        if ((studentData as any).aadharNo != null) {
          aadhaarNumber = String((studentData as any).aadharNo);
        }

        // Prefetch photo/signature blobs when available to avoid per-student photo-info calls inside template
        const sid = (studentData as any)?.id;
        const bulkPhotoInfo = typeof sid === "number" ? bulkPhotoInfoById.get(sid) : undefined;
        const skipStudentMediaFetch = typeof sid === "number" ? bulkPhotoInfoById.has(sid) : false;

        let photoObjectUrl: string | null = null;
        let signatureObjectUrl: string | null = null;
        try {
          if (bulkPhotoInfo?.fileName) {
            photoObjectUrl = await fetchBlobUrlForFileName(bulkPhotoInfo.fileName);
          }
          if (bulkPhotoInfo?.signature) {
            signatureObjectUrl = await fetchBlobUrlForFileName(bulkPhotoInfo.signature);
          }
        } catch (_) {
          // ignore
        }

        const templateData = {
          id: (studentData as any).id, // Add application ID for photo fetching
          photoUrl: photoObjectUrl,
          signatureUrl: signatureObjectUrl,
          skipStudentMediaFetch,
          dateOfIssue: new Date().toISOString().split('T')[0],
          isResident: true,
          isNRI: false,
          isNewEnrolment: true,
          isUpdateRequest: false,
          aadhaarNumber,
          fullName: (fullName || '').trim() || t('unknown_student'),
          careOf: (studentData as any).fatherName || (studentData as any).guardianName || '',
          houseNo: (studentData as any).houseNo || (studentData as any).houseNumber || '',
          street: (studentData as any).street || (studentData as any).address || '',
          landmark: (studentData as any).landmark || '',
          area: (studentData as any).area || (studentData as any).locality || '',
          village: (village || '').trim() || t(''),
          postOffice: (studentData as any).postOffice || '',
          district: (studentData as any).district || '',
          state: (studentData as any).state || '',
          pinCode: (studentData as any).pinCode || (studentData as any).pincode || '',
          dateOfBirth,
          mobileNo: (studentData as any).mobileNo || (studentData as any).phone || (studentData as any).mobile || '',
          gmail: (studentData as any).gmail || (studentData as any).email || '',
          certifierName: includeCertifierValues && schoolData?.schoolPrincipalName ? schoolData.schoolPrincipalName : '',
          certifierDesignation: includeCertifierValues && schoolData?.principalDesignation ? schoolData.principalDesignation : '',
          certifierOfficeAddress: includeCertifierValues && schoolData ? [
            fetchedSchoolName || schoolData.nameofSchool,
            schoolData.district,
            schoolData.state,
            schoolData.pinNo
          ].filter(part => part && part.trim() !== "").join(" ") : '',
          certifierContactNumber: includeCertifierValues && schoolData?.principalMobile ? schoolData.principalMobile : '',
          isGazettedOfficer: false,
          isHeadOfInstitution: includeCertifierValues,
        };

        // Check for cancellation before rendering
        if (isBulkDownloadCancelledRef.current) {
          break;
        }

        // 1) Render one page off-screen with providers
        const host = await renderCardOnce(templateData, BulkAadharTemplateWithContext);

        // Check for cancellation after rendering
        if (isBulkDownloadCancelledRef.current) {
          removeHost(host);
          break;
        }

        // Additional wait to ensure photos are fully loaded
        await new Promise(resolve => setTimeout(resolve, 500));

        // Check for cancellation after delay
        if (isBulkDownloadCancelledRef.current) {
          removeHost(host);
          break;
        }

        // 2) Capture that page
        const canvas = await html2canvas(host, {
          useCORS: true,
          allowTaint: true,
          background: '#ffffff',
          logging: false,
          width: Math.max(host.scrollWidth, host.offsetWidth),
          height: Math.max(host.scrollHeight, host.offsetHeight),
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.98);

        // Check for cancellation after canvas generation
        if (isBulkDownloadCancelledRef.current) {
          removeHost(host);
          break;
        }

        // 3) Add to PDF as full A4 page
        const pageW = A4_MM.w;
        const pageH = A4_MM.h;

        // Check for cancellation before adding to PDF
        if (isBulkDownloadCancelledRef.current) {
          removeHost(host);
          break;
        }

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, 0, pageW, pageH);
        pageIndex++;

        // 4) Cleanup DOM node
        removeHost(host);

        // Revoke blob URLs to prevent memory leak during bulk generation
        if (photoObjectUrl) URL.revokeObjectURL(photoObjectUrl);
        if (signatureObjectUrl) URL.revokeObjectURL(signatureObjectUrl);

        // Update progress after each card is processed
        setProgress(Math.round(((i + 1) / activeStudents.length) * 100));
      }

      // Check if cancelled before saving
      if (isBulkDownloadCancelledRef.current) {
        return;
      }

      pdf.save(filename);
      const successMsg = t('successfully_generated_bulk_aadhaar', { count: activeStudents.length, className, sessionName });
      // Show success message in top right corner using snackbar
      showSuccess(successMsg);
    } catch (error: any) {
      const errorMessage = error?.message || t('unknown_error');
      setError(t('failed_generate_bulk_pdf', { error: errorMessage }));
    } finally {
      setIsGenerating(false);
      setProgress(0);
      setCurrentStudent('');
      isBulkDownloadCancelledRef.current = false;
    }
  };

  const handleSessionChange = (event: any) => {
    const value = event.target.value;
    setBulkSelectedSession(value === '' ? '' : Number(value));
    // Clear error and success messages when session changes
    setError('');
    setSuccessMessage('');
    setLastStatusCode(null);
  };

  const handleClassChange = (event: any) => {
    const value = event.target.value;
    if (value === '') return setBulkSelectedClass('');
    const asNumber = Number(value);
    setBulkSelectedClass(Number.isFinite(asNumber) && String(value) === String(asNumber) ? asNumber : String(value));
    // Clear error and success messages when class changes
    setError('');
    setSuccessMessage('');
    setLastStatusCode(null);
  };

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: 'auto', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            {t('aadhar_card_management')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {!isStudent && (
              <Button
                variant="contained"
                sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, minWidth: '180px' }}
                onClick={handleOpenBulkModal}
              >
                {t('bulk_download_aadhar_cards')}
              </Button>
            )}
            <Button variant="contained" sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }} onClick={handleOpenModal}>
              {t('get_aadhar_card')}
            </Button>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('aadhaar_certificate_generator')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('generate_official_aadhaar')}
            {t('use_bulk_download')}
          </Typography>
        </Box>
      </Paper>

      {/* Individual Aadhar Number Input Modal */}
      <AadharCardFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        selectedClassId={layoutSelectedClassId}
        selectedSessionId={layoutSelectedSessionId}
      />

      {/* Bulk Download Modal - Only show for non-students */}
      {!isStudent && (
      <Dialog
        open={bulkModalOpen}
        onClose={handleCloseBulkModal}
        maxWidth="md"
        fullWidth
      >
          <DialogTitle>
            <Typography variant="h6" fontWeight="bold">
              {t('bulk_download_aadhar_cards')}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ position: 'relative' }}>
            <Box sx={{ py: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('select_class_session_download_aadhar')}
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t('session')}</InputLabel>
                  <Select
                    value={bulkSelectedSession}
                    label={t('session')}
                    onChange={handleSessionChange}
                    disabled={isGenerating}
                  >
                    {/* <MenuItem value="">
                    <em>{t('select_session_placeholder', { defaultValue: 'Select Session' })}</em>
                  </MenuItem> */}
                    {sessions.map((session: any) => (
                      <MenuItem key={session.id} value={session.id}>
                        {session.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t('class')}</InputLabel>
                  <Select
                    value={bulkSelectedClass}
                    label={t('class')}
                    onChange={handleClassChange}
                    disabled={!bulkSelectedSession || isGenerating || classesLoading}
                  >
                    {/* <MenuItem value="">
                    <em>{t('select_class_placeholder', { defaultValue: 'Select Class' })}</em>
                  </MenuItem> */}
                    {classesLoading ? (
                      <MenuItem disabled>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2">{t('loading_classes')}</Typography>
                        </Box>
                      </MenuItem>
                    ) : classesError ? (
                      <MenuItem disabled>
                        <Typography variant="body2" color="error">
                          {t('error_loading_classes')}
                        </Typography>
                      </MenuItem>
                    ) : classes.length === 0 ? (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                          {t('no_classes_found')}
                        </Typography>
                      </MenuItem>
                    ) : (
                      (() => {
                        const selectedSessionId =
                          typeof bulkSelectedSession === "string" ? Number(bulkSelectedSession) : bulkSelectedSession;

                        const base = classes
                          .filter((cls: any) => {
                            if (!selectedSessionId) return true;
                            const classSessionId = cls.sessionId ?? cls.session?.id ?? (cls.sessionId as any)?.id;
                            if (classSessionId !== null && classSessionId !== undefined) {
                              return Number(classSessionId) === Number(selectedSessionId);
                            }
                            return true;
                          })
                          .sort((a: any, b: any) => {
                            const classDiff = getClassSortValue(a?.name) - getClassSortValue(b?.name);
                            if (classDiff !== 0) return classDiff;
                            const sectionDiff = getSectionSortValue(a?.section) - getSectionSortValue(b?.section);
                            if (sectionDiff !== 0) return sectionDiff;
                            return (a?.name || "").localeCompare(b?.name || "");
                          });

                        const gradeNames = Array.from(new Set(base.map((c: any) => c?.name).filter(Boolean))) as string[];
                        gradeNames.sort((a, b) => getClassSortValue(a) - getClassSortValue(b));

                        const out: any[] = [];
                        out.push(
                          <MenuItem key="__class_all__" value="Class-ALL">
                            Class-ALL
                          </MenuItem>
                        );
                        for (const grade of gradeNames) {
                          out.push(
                            <MenuItem key={`${grade}-ALL`} value={`${grade}-ALL`}>
                              {grade}-ALL
                            </MenuItem>
                          );
                          base
                            .filter((c: any) => c?.name === grade)
                            .forEach((cls: any) => {
                              out.push(
                                <MenuItem key={cls.id} value={cls.id}>
                                  {cls.section ? `${cls.name} ${cls.section}` : cls.name}
                                </MenuItem>
                              );
                            });
                        }
                        return out;
                      })()
                    )}
                  </Select>
                </FormControl>

                {bulkSelectedClass && bulkSelectedSession && (() => {
                  const activeCount = bulkVisibleStudents.length;
                  const classInfo =
                    typeof bulkSelectedClass === "number"
                      ? classes.find((c: any) => c.id === bulkSelectedClass)
                      : null;
                  const className =
                    typeof bulkSelectedClass === "string"
                      ? bulkSelectedClass
                      : classInfo
                        ? classInfo.section
                          ? `${classInfo.name} ${classInfo.section}`
                          : classInfo.name
                        : "Unknown Class";
                  const sessionName = sessions.find((s: any) => s.id === bulkSelectedSession)?.name || "Unknown Session";
                  
                  return (
                    <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                      <Typography variant="body2" color="info.contrastText">
                        {t('found_active_students_in_class_session', { 
                          count: activeCount,
                          className: className,
                          sessionName: sessionName
                        })}
                      </Typography>
                    </Box>
                  );
                })()}

                {bulkVisibleStudents.length > 0 && (
                  <TableContainer sx={{ maxHeight: 280, mb: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ backgroundColor: "background.paper" }}>
                            <Checkbox
                              checked={bulkSelectAllChecked}
                              indeterminate={bulkSelectAllIndeterminate}
                              disabled={isGenerating}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setBulkSelectedStudentIds(
                                    new Set(bulkVisibleStudents.map((s: any) => s.id as number))
                                  );
                                } else {
                                  setBulkSelectedStudentIds(new Set());
                                }
                              }}
                              inputProps={{
                                "aria-label": (t("select_all") as string) || "Select All",
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", backgroundColor: "background.paper" }}>
                            {(t("select_all") as string) || "Select All"} / {t("student_name")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkVisibleStudents.map((student: any) => (
                          <TableRow key={student.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={bulkSelectedStudentIds.has(student.id)}
                                disabled={isGenerating}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setBulkSelectedStudentIds((prev) => {
                                    const next = new Set(prev);
                                    if (checked) next.add(student.id);
                                    else next.delete(student.id);
                                    return next;
                                  });
                                }}
                              />
                            </TableCell>
                            <TableCell>{student.studentName || student.name || ""}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

            {isGenerating && (
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    {t("generating_pdf")} {progress}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} />
                {currentStudent && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {t("processing").replace("{studentName}", currentStudent)}
                  </Typography>
                )}
              </Box>
            )}

            {/* Checkbox for Head of Institution - Only show when not generating */}
            {!isGenerating && (
              <Box sx={{ mt: 3, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeCertifierValues}
                      onChange={(e) => setIncludeCertifierValues(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                  <Typography variant="body2" sx={{ fontWeight: 'medium' ,marginTop: '12px'}}>
                      Head of recognized educational institution
                    </Typography>
                  }
                  sx={{
                    alignItems: 'flex-start',
                    '& .MuiFormControlLabel-label': {
                      marginTop: '2px'
                    }
                  }}
                />
              </Box>
            )}
          </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={isGenerating ? handleCancelBulkDownload : handleCloseBulkModal}
              variant="outlined"
            >
              {t("cancel")}
            </Button>

            <LoadingButton
              onClick={handleBulkDownload}
              variant="contained"
              sx={{
                minWidth: '200px',
                position: 'relative',
                backgroundColor: '#3092e3',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#b2ebff',
                  opacity: isGenerating ? 0.8 : 0.6,
                },
              }}
              loading={isGenerating}
              loadingPosition="start"
              // loadingIndicator={
              //   <Typography variant="body2" fontWeight="medium">
              //     {t("generating")}...
              //   </Typography>
              // }
              disabled={(() => {
                if (!bulkSelectedClass || !bulkSelectedSession || isGenerating) return true;
                if (studentsLoading) return true;
                if (bulkVisibleStudents.length === 0) return true;
                return bulkSelectedStudentIds.size === 0;
              })()}
            >
              {(() => {
                const activeStudentsCount = bulkSelectedStudentIds.size;
                const manualString = t('download_aadhaar_cards_manual', { count: activeStudentsCount });
                try {
                  const translationResult = t('download_aadhaar_cards', { count: activeStudentsCount });
                  if (translationResult.includes('{count}')) {
                    return manualString;
                  }
                  return translationResult;
                } catch {
                  return manualString;
                }
              })()}
            </LoadingButton>
          </DialogActions>

        </Dialog>
      )}

      {/* Bulk Preview Dialog */}
      <Dialog
        open={showBulkPreview}
        onClose={() => setShowBulkPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              {t('bulk_aadhaar_cards_preview')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({bulkTemplates.length} {t('students')})
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('preview_of_students', { count: bulkTemplates.length })}
            </Typography>
            {bulkTemplates.map((data, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, bgcolor: '#f9f9f9' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('card')} {index + 1}: {data.fullName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('aadhaar')}: {data.aadhaarNumber || t('not_provided')} |{' '}
                  {t('father')}: {data.careOf} |{' '}
                  {t('dob')}: {data.dateOfBirth} |{' '}
                  {t('village')}: {data.village}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setShowBulkPreview(false)}
            variant="outlined"
            disabled={isGenerating}
          >
            {t('cancel')}
          </Button>
          <LoadingButton
            onClick={handleBulkDownload}
            variant="contained"
            color="primary"
            loading={isGenerating}
            loadingPosition="start"
            // loadingIndicator={
            //   <Typography variant="body2" fontWeight="medium">
            //     {t("generating")}...
            //   </Typography>
            // }
            sx={{ minWidth: '200px' }}
          >
            {t('generate_pdf')} ({bulkTemplates.length} {t('cards')})
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AadharCardManagement;